import { Card, Group, Text, ThemeIcon } from "@mantine/core";

import { STAT_TILE_ICONS } from "@/viz/statTileIcons";

interface Props {
  label: string;
  value: number | null;
  /** Decimal places from the widget's `format:`; null means choose. */
  precision?: number | null;
  /** True while refetching: hold the previous value rather than flashing. */
  isRefreshing?: boolean;
  /** A Mantine color name from the config, or null for the default ink color. */
  color?: string | null;
  /** A key into STAT_TILE_ICONS, or null for no icon. */
  icon?: string | null;
  /** Smaller lines at the foot, in order — each a "label: value" pair
   * (hasValue) or a static note (label alone). */
  footer?: { label: string; value: number | null; hasValue: boolean }[];
}

/**
 * One number, which is the whole chart.
 *
 * When the data is a single current value, a tile is the right form and a
 * one-bar bar chart is the wrong one. There is no plot here, so this is the one
 * place in the app with no tooltip and no table twin — the value is already
 * displayed as text.
 *
 * Not a hero figure: those are >= 48px and there is exactly one per view. These
 * sit in a row of peers, so they stay at heading scale.
 */
export function StatTile({
  label,
  value,
  precision,
  isRefreshing = false,
  color,
  icon,
  footer = [],
}: Props) {
  // Unknown/omitted icon name renders no icon rather than a broken one —
  // the same graceful-degradation rule as everywhere else a config value
  // maps to a client-side lookup.
  const Icon = icon ? STAT_TILE_ICONS[icon] : undefined;

  return (
    <Card
      withBorder
      padding="lg"
      h="100%"
      // A colored left accent rather than a filled or bordered card: it
      // reads as identity, not alarm, and does not fight the card shadow's
      // own sense of elevation. Undeclared (color is null) stays exactly the
      // plain card it always was — color is opt-in per tile, not a redesign.
      style={
        color
          ? { borderLeft: `3px solid var(--mantine-color-${color}-6)` }
          : undefined
      }
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Text size="sm" c="dimmed">
          {label}
        </Text>
        {Icon && (
          <ThemeIcon variant="light" color={color ?? "gray"} size="md" radius="md">
            <Icon size={16} />
          </ThemeIcon>
        )}
      </Group>
      <Text
        fw={600}
        mt={6}
        c={color ?? undefined}
        style={{
          fontSize: "1.75rem",
          lineHeight: 1.15,
          // Deliberately NOT tabular-nums: equal-width digits make a number
          // like 121 look loose at display sizes. Tabular figures are for
          // columns that must align vertically, which this is not.
          opacity: isRefreshing ? 0.55 : 1,
          transition: "opacity 150ms ease",
        }}
      >
        {value === null ? "—" : format(value, precision)}
      </Text>
      {footer.length > 0 && (
        <Group gap="md" wrap="wrap" mt={4}>
          {footer.map((item, index) => (
            <Text key={index} size="xs" c="dimmed">
              {item.hasValue ? (
                <>
                  {item.label}:{" "}
                  <Text span fw={500} c="dimmed">
                    {item.value == null ? "—" : format(item.value)}
                  </Text>
                </>
              ) : (
                item.label
              )}
            </Text>
          ))}
        </Group>
      )}
    </Card>
  );
}

/**
 * A declared precision always wins — it came from the dashboard file.
 * Otherwise the full integer, with ordinary locale grouping.
 */
function format(value: number, precision?: number | null): string {
  if (precision != null) {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    });
  }
  return value.toLocaleString();
}
